/**
 * WAFR (Well-Architected Framework Review) module schema — a curated question
 * set per Well-Architected pillar plus a workload-definition section.
 *
 * Backend has the parallel section/field skeleton in
 * `backend/app/domain/wafr_schema.py`; `backend/tests/test_schema_in_sync.py`
 * asserts the two stay in sync (including which fields are `required`).
 *
 * The shared technical-baseline sections (`companyProfileSchema.ts`) render
 * inside the WAFR questionnaire too, but store on the company profile.
 */

import type { ChoiceOption, SectionDef } from './types'

/** Options whose value is their label. */
const choices = (values: string[]): ChoiceOption[] => values.map((v) => ({ value: v, label: v }))

/** Shared adherence scale for the pillar questions — how far along the
 *  practice is, not whether a tool is installed. */
const ADHERENCE_OPTIONS = choices([
  'Not started', 'Partially in place', 'Mostly in place', 'Fully adopted',
])

const CRITICALITY_OPTIONS = choices([
  'Mission-critical', 'Business-important', 'Internal', 'Experimental',
])

const DEPLOYMENT_PRACTICE_OPTIONS = choices([
  'Automated CI/CD pipeline', 'Infrastructure as code', 'Automated testing gates',
  'Blue/green or canary deployments', 'Automated rollback', 'Frequent small changes',
  'Manual deployments',
])

const DETECTION_CONTROL_OPTIONS = choices([
  'CloudTrail', 'GuardDuty', 'Security Hub', 'AWS Config', 'Inspector', 'Macie',
  'VPC Flow Logs', 'Third-party SIEM',
])

const PRICING_MODEL_OPTIONS = choices([
  'Savings Plans', 'Reserved Instances', 'Spot Instances', 'On-Demand only',
])

/** Company-owned baseline sections rendered inside the WAFR questionnaire —
 *  mirrors `wafr_schema.SHARED_SECTIONS` (sync-tested). */
export const WAFR_SHARED_SECTION_KEYS = [
  'architecture_baseline', 'security_posture', 'operations_practices',
]

export const WAFR_SECTIONS: SectionDef[] = [
  {
    key: 'workload_definition',
    number: 1,
    title: 'Workload Definition',
    navTitle: 'Workload',
    description:
      'Which workload this review covers and who owns it. A Well-Architected review is scoped to one workload, not the whole estate — define the boundary before answering the pillars.',
    fields: [
      {
        key: 'workload_name',
        label: 'Workload name',
        hint:
          'A short name for the workload under review — the system boundary every pillar answer refers back to.',
        type: { kind: 'text', placeholder: 'Customer-facing e-commerce platform' },
        priority: 'required',
      },
      {
        key: 'workload_description',
        label: 'Workload description',
        hint:
          'What the workload does, who its users are, and roughly which components are in scope — enough for a reader to know where the review boundary sits.',
        type: { kind: 'textarea', rows: 3 },
        priority: 'required',
      },
      {
        key: 'business_criticality',
        label: 'Business criticality',
        hint:
          'How much the business hurts when this workload is down — calibrates how strict the reliability and security findings should be.',
        type: { kind: 'radio', options: CRITICALITY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'aws_regions',
        label: 'AWS regions',
        hint:
          'The regions the workload runs in today, one per line — multi-region posture changes the reliability and sustainability answers.',
        type: { kind: 'list', placeholder: 'us-east-1' },
        priority: 'recommended',
      },
      {
        key: 'review_owner',
        label: 'Review owner',
        hint:
          'The person on the customer side accountable for this review and for acting on its findings. Note: when a Well-Architected partner ID is configured in admin settings, that ID — not this name — is what AWS records as the workload’s Review Owner, because that is how AWS credits the review to us.',
        type: { kind: 'text', placeholder: 'Jane Doe — Head of Platform' },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'operational_excellence',
    number: 2,
    title: 'Operational Excellence',
    navTitle: 'Ops Excellence',
    description:
      'How the team runs and improves the workload: priorities, health visibility, runbooks, deployment practices, and learning from failure.',
    fields: [
      {
        key: 'ops_priorities_defined',
        label: 'Operational priorities defined',
        hint:
          'Whether the team has defined what "operating well" means for this workload — priorities and trade-offs agreed with the business, not just assumed.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'workload_health_monitoring',
        label: 'Workload health monitoring',
        hint:
          'Whether the team can tell the workload is healthy from metrics and alerts — before the customers tell them it is not.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'runbooks_playbooks',
        label: 'Runbooks & playbooks',
        hint:
          'Whether routine operations and incident responses are written down as runbooks/playbooks rather than living in one engineer\'s head.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'deployment_practices',
        label: 'Deployment practices',
        hint:
          'Which safe-deployment practices the team actually uses for this workload — small, frequent, reversible changes are the pillar\'s ideal.',
        type: { kind: 'multiselect', options: DEPLOYMENT_PRACTICE_OPTIONS, allowOther: true },
        priority: 'recommended',
      },
      {
        key: 'failure_learning',
        label: 'Learning from failure',
        hint:
          'Whether incidents end in a blameless review that produces real improvements, or just in a restored service and a shrug.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Operational excellence notes',
        hint:
          'Anything else about how this workload is operated that the questions above miss.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'security',
    number: 3,
    title: 'Security',
    description:
      'How the workload protects data and systems: identity, data protection, detection, infrastructure protection, and incident response.',
    fields: [
      {
        key: 'identity_access_mgmt',
        label: 'Identity & access management',
        hint:
          'Whether human and machine access to this workload follows least privilege — scoped roles, no shared credentials, no standing admin access.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'data_protection',
        label: 'Data protection',
        hint:
          'Whether the workload\'s data is classified and protected at rest and in transit, with key management appropriate to its sensitivity.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'detection_controls',
        label: 'Detection controls',
        hint:
          'Which detection services are enabled for the accounts this workload runs in — you cannot respond to what you cannot see.',
        type: { kind: 'multiselect', options: DETECTION_CONTROL_OPTIONS, allowOther: true },
        priority: 'recommended',
      },
      {
        key: 'infrastructure_protection',
        label: 'Infrastructure protection',
        hint:
          'Whether the network and compute layers enforce defense in depth — segmented VPCs, security groups scoped to need, hardened images, edge protection.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'incident_response_plan',
        label: 'Incident response plan',
        hint:
          'Whether a security-incident plan exists for this workload and has actually been exercised — a game day beats a binder.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Security notes',
        hint:
          'Anything else about the workload\'s security posture that the questions above miss.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'reliability',
    number: 4,
    title: 'Reliability',
    description:
      'Whether the workload recovers from failure and meets demand: backups, fault isolation, scaling, quotas, and DR testing.',
    fields: [
      {
        key: 'backup_strategy',
        label: 'Backup strategy',
        hint:
          'Whether the workload\'s data is backed up to meet defined recovery objectives, and restores are tested — recovery is the requirement, backups are just the means.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'fault_isolation',
        label: 'Fault isolation',
        hint:
          'Whether the workload uses fault-isolation boundaries — multiple AZs, cell or bulkhead patterns — so one component failing does not take everything down.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'scaling_approach',
        label: 'Scaling approach',
        hint:
          'Whether the workload scales automatically with demand rather than relying on humans noticing load and adding capacity.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'service_quotas_managed',
        label: 'Service quotas managed',
        hint:
          'Whether service quotas and limits are tracked and raised ahead of need — hitting an unnoticed quota is a self-inflicted outage.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'dr_testing',
        label: 'DR testing',
        hint:
          'Whether disaster-recovery procedures are tested on a schedule — an untested failover plan usually fails the first real test.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Reliability notes',
        hint:
          'Anything else about the workload\'s resilience that the questions above miss.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'performance_efficiency',
    number: 5,
    title: 'Performance Efficiency',
    navTitle: 'Performance',
    description:
      'Whether resources match the workload: right-sizing, storage selection, caching, and performance testing.',
    fields: [
      {
        key: 'right_sizing',
        label: 'Right-sizing',
        hint:
          'Whether compute and database resources are sized from measured utilization rather than guesses — and re-evaluated as demand changes.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'storage_selection',
        label: 'Storage selection',
        hint:
          'Whether storage choices (S3 classes, EBS types, database engines) match each dataset\'s access pattern instead of defaulting to one tier for everything.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'caching_strategy',
        label: 'Caching strategy',
        hint:
          'Whether caching (CloudFront, ElastiCache, application-level) is used deliberately where it cuts latency and load — the cheapest request is the one never served.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'performance_testing',
        label: 'Performance testing',
        hint:
          'Whether performance is verified with load tests against defined targets before changes ship, not discovered in production.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Performance notes',
        hint:
          'Anything else about the workload\'s performance characteristics that the questions above miss.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'cost_optimization',
    number: 6,
    title: 'Cost Optimization',
    navTitle: 'Cost',
    description:
      'Whether spend is visible and deliberate: cost attribution, lifecycle cleanup, pricing models, and governance.',
    fields: [
      {
        key: 'cost_visibility',
        label: 'Cost visibility',
        hint:
          'Whether the team can see what this workload costs — tagging, Cost Explorer or CUR dashboards, and costs attributed to owners who can act on them.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'resource_lifecycle',
        label: 'Resource lifecycle management',
        hint:
          'Whether unused and idle resources are found and retired on a cadence — orphaned volumes, idle instances, and stale environments are pure waste.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'pricing_models',
        label: 'Pricing models in use',
        hint:
          'Which commitment and purchase models the workload benefits from today — steady-state usage left on On-Demand is money on the table.',
        type: { kind: 'multiselect', options: PRICING_MODEL_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'cost_governance',
        label: 'Cost governance',
        hint:
          'Whether budgets, alerts, and review cadences exist so cost surprises surface in days, not at the end of the quarter.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Cost notes',
        hint:
          'Anything else about the workload\'s cost profile that the questions above miss.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'sustainability',
    number: 7,
    title: 'Sustainability',
    description:
      'Whether the workload minimizes its resource footprint: region choice, utilization, and data lifecycle.',
    fields: [
      {
        key: 'region_selection',
        label: 'Region selection',
        hint:
          'Whether region choices consider carbon intensity alongside latency and cost — some regions run on substantially cleaner grids.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'utilization_optimization',
        label: 'Utilization optimization',
        hint:
          'Whether the workload maximizes the utilization of what it provisions — scaling to demand and consolidating idle capacity wastes fewer resources, and less money.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'data_lifecycle_policies',
        label: 'Data lifecycle policies',
        hint:
          'Whether data is tiered and expired by policy — keeping everything forever on hot storage burns energy and budget for data nobody reads.',
        type: { kind: 'radio', options: ADHERENCE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Sustainability notes',
        hint:
          'Anything else about the workload\'s resource footprint that the questions above miss.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
]
